import type { PointerEvent as ReactPointerEvent } from 'react';

import { MapOverlay } from '@/components/maps';
import type { MapPoint } from '@/lib/maps/types';

import { MapMarkerTooltip } from './map-marker-tooltip';
import styles from './marked-points.module.scss';

export type MarkedPointsProps = {
  /** The **Point**s to draw, in the order they were marked — which is the order they are numbered
   *  in, and the order they connect in. During a drag this is the shape with the dragged **Point**
   *  at its LIVE position, so the edges follow the pointer. */
  points: readonly MapPoint[];
  /** Whose tooltip is open, or `null` for none. Only ever one (113600). Also the **Point** painted
   *  black while it is set — see the derivation below. */
  selectedIndex: number | null;
  /** Whether the open tooltip offers a delete control at all. `false` at exactly three **Point**s
   *  (113600; grilling **D7** — absent, not greyed). */
  canDelete: boolean;
  onSelect: (index: number) => void;
  onDelete: (index: number) => void;
  /**
   * A marker's own press, forwarded raw. The gesture — threshold, live preview, commit or revert —
   * belongs to the page, which owns the projection, the container's rect and the placement check;
   * this component stays a render-and-report target, the same division `onSelect`/`onDelete` draw.
   */
  onPointerDown?: (index: number, event: ReactPointerEvent<HTMLButtonElement>) => void;
};

/**
 * The marked **Point**s on the Field Mapping canvas (Figma `594:533335`–`594:533340` — a 21 px
 * circle carrying its number, white with a black ring, except one Point which inverts to a black
 * fill with a white ring), each its own click target with its own tooltip.
 *
 * **Two things stay derived rather than stored**, which is what makes several criteria free:
 *
 * - **the number is the array index**, so a delete renumbers by removing an element and nothing
 *   else;
 * - **the black Point is `selectedIndex ?? points.length - 1`**, so it is the clicked **Point** while
 *   a tooltip is open and the most recently marked one otherwise. That single expression is all of
 *   "clicking a Point turns it black", "only ever one black", "after a delete the highlight moves to
 *   whichever Point is then most recent", and Escape "leaving the black-highlight behaviour
 *   unchanged" — closing the tooltip clears the selection and the rule falls back on its own.
 *
 * **The tooltip is a sibling overlay inside the same `<MapOverlay>`, not the shared antd
 * `<Tooltip>`** (grilling Q11): it has to move with the **Point** on every camera frame, which
 * antd's floating popup does not subscribe to. Its values come from that component's dark
 * appearance all the same, so the two dark tooltips in this app are the same tooltip. `.stack`
 * carries the centring transform that used to sit on `.point`, so the numbered circle stays on its
 * coordinate whether or not a tooltip renders above it — the idiom `device-markers.module.scss`
 * already uses for a name chip above a pin.
 *
 * No `role="tooltip"`: it contains a real `<button>`, and WAI-ARIA forbids focusable content inside
 * that role. The tooltip itself lives in `<MapMarkerTooltip>`, which this component and the **Mid
 * Point** marker both call.
 */
export function MarkedPoints({
  points,
  selectedIndex,
  canDelete,
  onSelect,
  onDelete,
  onPointerDown,
}: MarkedPointsProps) {
  // DERIVED, never stored — see the docblock. `?? points.length - 1` is -1 for an empty array,
  // which matches no index, which is correct: nothing is black when nothing is marked.
  const blackIndex = selectedIndex ?? points.length - 1;

  return (
    <>
      {points.map((point, index) => {
        const state = index === blackIndex ? 'recent' : 'earlier';
        const label = index + 1;
        return (
          // A marked Point carries no identity of its own; its position in the marked order is its
          // only stable key — and it is the RIGHT key here: a delete renumbers, and an index key is
          // what lets React reuse the DOM the renumbering is supposed to relabel.
          <MapOverlay key={index} point={point}>
            <span className={styles.stack}>
              {selectedIndex === index ? (
                <MapMarkerTooltip
                  title={`Point ${label}`}
                  point={point}
                  canDelete={canDelete}
                  deleteLabel={`Delete point ${label}`}
                  onDelete={() => onDelete(index)}
                />
              ) : null}
              <button
                type="button"
                className={state === 'recent' ? `${styles.point} ${styles.recent}` : styles.point}
                data-state={state}
                aria-label={`Point ${label}`}
                onClick={() => onSelect(index)}
                onPointerDown={(event) => onPointerDown?.(index, event)}
              >
                {label}
              </button>
            </span>
          </MapOverlay>
        );
      })}
    </>
  );
}
